// Assuming you have a SubscriptionContext where you keep your subscription data

import AuthorizationScope from '@airimpact/types/authorizationScopes'
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { useProjectDetailsContext } from './useProjectDetails'
import { useWorkspaceContext } from './useWorkspace'

export const FeaturesName = {
  file: AuthorizationScope.WorkspaceFile._id,
  monitoringSystem: AuthorizationScope.WorkspaceProjectIntegrationDroneShots._id,
  user: AuthorizationScope.WorkspaceUser._id,
  userWorkspaceRole: AuthorizationScope.WorkspaceRole._id,
  workspace: AuthorizationScope.Workspace._id,
  workspaceApiKey: AuthorizationScope.WorkspaceApiKey._id,
  workspaceEmployee: AuthorizationScope.WorkspaceEmployee._id,
  workspaceRole: AuthorizationScope.WorkspaceRole._id,
  workspaceProject: AuthorizationScope.WorkspaceProject._id,
  workspaceProjectDesign: AuthorizationScope.WorkspaceProject._id,
  workspaceProjectAiAdvisor: AuthorizationScope.WorkspaceProjectAiAdviser._id,
  workspaceProjectBlock: AuthorizationScope.WorkspaceProjectCarbonRemoval._id,
  workspaceProjectCertification: AuthorizationScope.WorkspaceProjectCertification._id,
  workspaceProjectDatasetTransaction: AuthorizationScope.WorkspaceProjectDatasetNft._id,
  workspaceProjectGallery: AuthorizationScope.WorkspaceProjectGallery._id,
  workspaceProjectIntegrationDroneShots: AuthorizationScope.WorkspaceProjectIntegrationDroneShots._id,
  workspaceProjectIntegrationCluey: AuthorizationScope.WorkspaceProjectIntegrationCluey._id,
  workspaceProjectAreas: AuthorizationScope.WorkspaceProjectIntegrationGeoData._id,
  workspaceProjectIntegrationSatelliteImagery: AuthorizationScope.WorkspaceProjectIntegrationSatelliteImagery._id,
  workspaceProjectMilestone: AuthorizationScope.WorkspaceProjectMilestone._id,
  workspaceProjectAssetRegistry: AuthorizationScope.WorkspaceProjectDatasetNft._id,
  workspaceProjectUpdate: AuthorizationScope.WorkspaceProjectUpdate._id,
  workspaceSubscription: AuthorizationScope.WorkspaceSubscription._id,
  workspaceProjectDocumentation: AuthorizationScope.WorkspaceProjectAiReportAssistant._id,
  WorkspaceProjectStakeholders: AuthorizationScope.WorkspaceProjectStakeholders._id,
  WorkspaceProjectImpactFramework: AuthorizationScope.WorkspaceProjectImpactFramework._id,
  WorkspaceProjectCommunitiesPopulation: AuthorizationScope.WorkspaceProjectCommunitiesPopulation._id,
  WorkspaceProjectCommunitiesSocialSafeguard: AuthorizationScope.WorkspaceProjectCommunitiesSocialSafeguard._id,
  WorkspaceProjectLandUseAndArea: AuthorizationScope.WorkspaceProjectLandUseAndArea._id,
  WorkspaceProjectLandUseAndCarbonRights: AuthorizationScope.WorkspaceProjectLandUseAndCarbonRights._id,
  WorkspaceProjectMetric: AuthorizationScope.WorkspaceProjectMetric._id,
  WorkspaceProjectSdg: AuthorizationScope.WorkspaceProjectSdg._id,
  WorkspaceSubscription: AuthorizationScope.WorkspaceSubscription._id,
  WorkspaceOrganization: AuthorizationScope.WorkspaceOrganization._id,
} as const

export type FeaturesNameKey = keyof typeof FeaturesName
export type FeaturesNameValue = (typeof FeaturesName)[keyof typeof FeaturesName]

const routeFeatureMap = {
  '/milestone': [FeaturesName.workspaceProjectMilestone],
  '/project-area': [FeaturesName.workspaceProjectAreas],
  '/updates': [FeaturesName.workspaceProjectUpdate],
  '/monitoring-data': [FeaturesName.workspaceProjectIntegrationDroneShots],
  '/create-asset': [FeaturesName.workspaceProjectAssetRegistry],
  '/certification': [FeaturesName.workspaceProjectCertification],
  '/questionnaire': [FeaturesName.workspaceProjectAiAdvisor],
  '/ai/chat': [FeaturesName.workspaceProjectAiAdvisor],
  '/gallery': [FeaturesName.workspaceProjectGallery],
  '/carbon-tracker': [FeaturesName.workspaceProjectBlock],
  '/carbon-tracker/estimates-calculator': [FeaturesName.workspaceProjectBlock],
  '/knowledge': [FeaturesName.workspaceProjectDocumentation],
  '/assets': [FeaturesName.workspaceProjectAssetRegistry],
  '/documents': [FeaturesName.workspaceProjectDocumentation],
  '/organizations': [FeaturesName.WorkspaceOrganization],
  '/impact-framework': [FeaturesName.WorkspaceProjectImpactFramework],
  '/land-and-carbon-rights': [FeaturesName.WorkspaceProjectLandUseAndCarbonRights],
  '/area-and-land-use': [FeaturesName.WorkspaceProjectLandUseAndArea],
  '/sdg': [FeaturesName.WorkspaceProjectSdg],
  '/stakeholders': [FeaturesName.WorkspaceProjectStakeholders],
}

export function useFeature(featureScope?: FeaturesNameValue) {
  const { selectedWorkspaceDetails } = useWorkspaceContext()
  const { selectedProject } = useProjectDetailsContext()

  const subscriptionPlanFeaturesProject = selectedProject?.subscription?.features || []
  const subscriptionPlanFeaturesWorkspace = selectedWorkspaceDetails.subscription?.features || []
  const subscriptionPlanFeatures = [...subscriptionPlanFeaturesProject, ...subscriptionPlanFeaturesWorkspace]
  const featureMap = useMemo(
    () => subscriptionPlanFeatures?.reduce((map, feature) => ({ ...map, [feature.scope]: true }), {}),
    [selectedProject, selectedWorkspaceDetails],
  )
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return featureMap[featureScope] || false
}

export function useRouteFeature() {
  const { selectedWorkspaceDetails } = useWorkspaceContext()

  const { selectedProject } = useProjectDetailsContext()
  const location = useLocation()
  const hasFullAccess = useMemo(() => {
    const subscriptionPlanFeaturesProject = selectedProject?.subscription?.features || []
    const subscriptionPlanFeaturesWorkspace = selectedWorkspaceDetails.subscription?.features || []
    const subscriptionPlanFeatures = [...subscriptionPlanFeaturesProject, ...subscriptionPlanFeaturesWorkspace]
    if (!location || subscriptionPlanFeatures.length === 0) return true
    const featureScopesKey = Object.keys(routeFeatureMap).find((route) => location.pathname.includes(route))
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const featureScopes = featureScopesKey ? routeFeatureMap[featureScopesKey] : []

    return featureScopes.every((scope: FeaturesNameKey) =>
      subscriptionPlanFeatures.find((feature) => feature.scope === scope),
    )
  }, [location, selectedWorkspaceDetails, selectedProject])

  return hasFullAccess
}
